import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Select,
  Modal,
  Badge,
  Pagination,
  Alert,
} from '../../components/ui';
import {
  getJabatanList,
  createJabatan,
  updateJabatan,
  deleteJabatan,
} from '../../features/master/api';
import { RoleCode, type Jabatan } from '@app/shared';
import { useAuth } from '../../features/auth/use-auth';

type FormState = {
  id?: number;
  kode: string;
  nama: string;
  deskripsi: string;
  status_aktif: boolean;
};

export function JabatanPage() {
  const { user } = useAuth();
  const canManage = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const [rows, setRows] = useState<Jabatan[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    kode: '',
    nama: '',
    deskripsi: '',
    status_aktif: true,
  });
  const [formError, setFormError] = useState<string | null>(null);

  const load = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getJabatanList({
        page,
        limit: 10,
        search: search || undefined,
        status_aktif: statusFilter ? statusFilter === 'true' : undefined,
      });
      setRows(result.rows);
      setMeta(result.meta);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [search, statusFilter]);

  const openCreate = () => {
    setForm({ kode: '', nama: '', deskripsi: '', status_aktif: true });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row: Jabatan) => {
    setForm({
      id: row.id,
      kode: row.kode,
      nama: row.nama,
      deskripsi: row.deskripsi ?? '',
      status_aktif: row.status_aktif,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.kode.trim() || !form.nama.trim()) {
      setFormError('Kode dan nama wajib diisi');
      return;
    }
    try {
      const body = {
        kode: form.kode.trim(),
        nama: form.nama.trim(),
        deskripsi: form.deskripsi.trim() || null,
      };
      if (form.id) {
        await updateJabatan(form.id, { ...body, status_aktif: form.status_aktif });
        setSuccess('Jabatan berhasil diperbarui');
      } else {
        await createJabatan(body as typeof body & { kode: string });
        setSuccess('Jabatan berhasil dibuat');
      }
      setModalOpen(false);
      load(meta.page);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gagal menyimpan');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Yakin ingin menghapus jabatan ini?')) return;
    try {
      await deleteJabatan(id);
      setSuccess('Jabatan berhasil dihapus');
      load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus');
    }
  };

  return (
    <AdminLayout title="Master Jabatan">
      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      <Card>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Input
            placeholder="Cari jabatan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: '160px' }}
          >
            <option value="">Semua status</option>
            <option value="true">Aktif</option>
            <option value="false">Nonaktif</option>
          </Select>
          {canManage && <Button onClick={openCreate}>Tambah Jabatan</Button>}
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Kode</Th>
              <Th>Nama</Th>
              <Th>Deskripsi</Th>
              <Th>Status</Th>
              {canManage && <Th style={{ textAlign: 'right' }}>Aksi</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>{row.kode}</Td>
                <Td>{row.nama}</Td>
                <Td>{row.deskripsi || '-'}</Td>
                <Td>
                  <Badge color={row.status_aktif ? 'green' : 'gray'}>
                    {row.status_aktif ? 'Aktif' : 'Nonaktif'}
                  </Badge>
                </Td>
                {canManage && (
                  <Td style={{ textAlign: 'right' }}>
                    <Button variant="secondary" onClick={() => openEdit(row)}>
                      Ubah
                    </Button>{' '}
                    <Button variant="danger" onClick={() => handleDelete(row.id)}>
                      Hapus
                    </Button>
                  </Td>
                )}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <Td colSpan={canManage ? 5 : 4} style={{ textAlign: 'center' }}>
                  Tidak ada data
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        {loading && <p style={{ textAlign: 'center', marginTop: '1rem' }}>Memuat...</p>}
        <Pagination page={meta.page} totalPages={meta.total_pages} onChange={load} />
      </Card>

      <Modal
        open={modalOpen && canManage}
        onClose={() => setModalOpen(false)}
        title={form.id ? 'Ubah Jabatan' : 'Tambah Jabatan'}
      >
        <form onSubmit={handleSubmit}>
          {formError && <Alert type="error">{formError}</Alert>}
          <Input
            label="Kode"
            value={form.kode}
            onChange={(e) => setForm({ ...form, kode: e.target.value })}
            disabled={!!form.id}
            required
          />
          <Input
            label="Nama"
            value={form.nama}
            onChange={(e) => setForm({ ...form, nama: e.target.value })}
            required
          />
          <Input
            label="Deskripsi"
            value={form.deskripsi}
            onChange={(e) => setForm({ ...form, deskripsi: e.target.value })}
          />
          <Select
            label="Status"
            value={String(form.status_aktif)}
            onChange={(e) => setForm({ ...form, status_aktif: e.target.value === 'true' })}
          >
            <option value="true">Aktif</option>
            <option value="false">Nonaktif</option>
          </Select>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button variant="secondary" onClick={() => setModalOpen(false)} type="button">
              Batal
            </Button>
            <Button type="submit">Simpan</Button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
}
